// Routes every identity-sensitive RPC through the Clerk-verified `secure-rpc`
// edge function. The database has revoked EXECUTE on these functions from the
// anon/authenticated roles, so a spoofed `_clerk_user_id` can no longer reach
// them: the bridge derives the caller identity from the verified Clerk token.
import { supabase } from "@/integrations/supabase/client";
import { PROTECTED_RPC_SET } from "@/lib/protectedRpcs";

type TokenGetter = (options?: any) => Promise<string | null>;

let tokenGetter: TokenGetter | null = null;

export function registerClerkTokenGetter(getToken: TokenGetter | null) {
  tokenGetter = getToken;
}

async function callSecureRpc(fn: string, args: Record<string, unknown>) {
  try {
    const token =
      (await tokenGetter?.()) ||
      (await (window as any)?.Clerk?.session?.getToken?.().catch(() => null)) ||
      null;
    const { data, error } = await supabase.functions.invoke("secure-rpc", {
      body: { fn, args },
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    });
    if (error) return { data: null, error: { message: error.message } };
    if (!data?.success) return { data: null, error: { message: data?.error || "Request failed" } };
    return { data: data.data ?? null, error: null };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message } };
  }
}

let installed = false;

export function installRpcGuard() {
  if (installed) return;
  installed = true;

  const client = supabase as any;
  const originalRpc = client.rpc.bind(client);

  client.rpc = (fn: string, args?: Record<string, unknown>, options?: unknown) => {
    if (!PROTECTED_RPC_SET.has(fn)) return originalRpc(fn, args, options);

    const promise = callSecureRpc(fn, args ?? {});
    // Mimic the PostgREST builder surface used across the app.
    return Object.assign(promise, {
      then: promise.then.bind(promise),
      catch: promise.catch.bind(promise),
      finally: promise.finally.bind(promise),
      single: async () => {
        const res = await promise;
        const row = Array.isArray(res.data) ? res.data[0] ?? null : res.data;
        return { ...res, data: row };
      },
      maybeSingle: async () => {
        const res = await promise;
        const row = Array.isArray(res.data) ? res.data[0] ?? null : res.data;
        return { ...res, data: row };
      },
    });
  };
}

// Install eagerly on import so no call site can bypass the bridge.
installRpcGuard();
